import React from 'react';
import { defineMessages, injectIntl, useIntl } from 'react-intl';
import { Form, Field } from 'react-final-form';
import Select from 'react-select';

import FormGroup2 from 'components/form/FormGroup';
import FormError from 'components/form/FormError';
import FormSubmit from 'components/form/FormSubmit';
import TextField from 'components/form/TextField';

import { availableLocales } from 'actions/intl';
import _ from 'lodash';

const messages = defineMessages({
  PLACEHOLDER_COMPANY_NAME: {
    id: 'CompanyUpdateComponent.PLACEHOLDER_COMPANY_NAME',
    defaultMessage: 'Unternehmensname',
  },
  LABEL_SAVE_CHANGES: {
    id: 'CompanyUpdateComponent.LABEL_SAVE_CHANGES',
    defaultMessage: 'Änderungen speichern',
  },
  VALIDATION_FIELD_REQUIRED: {
    id: 'CompanyUpdateComponent.VALIDATION_FIELD_REQUIRED',
    defaultMessage: 'Dieses Feld ist erforderlich.',
  },
  LABEL_LANGUAGE: {
    id: 'CompanyUpdateComponent.LABEL_LANGUAGE',
    defaultMessage: 'Sprache zur Erzeugung von Dokumenten',
  },
  LOCALE_DE: {
    id: 'CompanyUpdateComponent.LOCALE_DE',
    defaultMessage: 'Deutsch',
  },
  LOCALE_EN: {
    id: 'CompanyUpdateComponent.LOCALE_EN',
    defaultMessage: 'Englisch',
  },
  LOCALE_FR: {
    id: 'CompanyUpdateComponent.LOCALE_FR',
    defaultMessage: 'Französisch',
  },
  LOCALE_ES: {
    id: 'CompanyUpdateComponent.LOCALE_ES',
    defaultMessage: 'Spanisch',
  },
  LABEL_TRIAL_DAYS: {
    id: 'CompanyUpdateComponent.LABEL_TRIAL_DAYS',
    defaultMessage: 'Testzeitraum (Tage)',
  },
  LABEL_EMAIL_TEMPLATE: {
    id: 'CompanyUpdateComponent.LABEL_EMAIL_TEMPLATE',
    defaultMessage: 'Standard E-Mail-Vorlage (nutze [LINK] für den Raum-Link)',
  },
  LABEL_SMS_TEMPLATE: {
    id: 'CompanyUpdateComponent.LABEL_SMS_TEMPLATE',
    defaultMessage: 'Standard SMS-Vorlage (nutze [LINK] für den Raum-Link)',
  },
  LABEL_MAX_USERS: {
    id: 'CompanyUpdateComponent.LABEL_MAX_USERS',
    defaultMessage: 'Maximal erlaubte Nutzer',
  },
  LABEL_ALLOWED_LANGUAGES: {
    id: 'CompanyUpdateComponent.LABEL_ALLOWED_LANGUAGES',
    defaultMessage: 'Erlaubte Sprachen (kommagetrennt, z.B. de,en,es)',
  },
  LABEL_RETENTION_DAYS: {
    id: 'CompanyUpdateComponent.LABEL_RETENTION_DAYS',
    defaultMessage: 'Automatische Datenlöschung nach',
  },
  RETENTION_1_DAY: { id: 'CompanyUpdateComponent.RETENTION_1_DAY', defaultMessage: '1 Tag' },
  RETENTION_1_MONTH: { id: 'CompanyUpdateComponent.RETENTION_1_MONTH', defaultMessage: '1 Monat' },
  RETENTION_3_MONTHS: { id: 'CompanyUpdateComponent.RETENTION_3_MONTHS', defaultMessage: '3 Monate' },
  RETENTION_6_MONTHS: { id: 'CompanyUpdateComponent.RETENTION_6_MONTHS', defaultMessage: '6 Monate' },
  RETENTION_1_YEAR: { id: 'CompanyUpdateComponent.RETENTION_1_YEAR', defaultMessage: '1 Jahr (Standard)' },
  RETENTION_2_YEARS: { id: 'CompanyUpdateComponent.RETENTION_2_YEARS', defaultMessage: '2 Jahre' },
  LABEL_PRIVACY_URL: { id: 'CompanyUpdateComponent.LABEL_PRIVACY_URL', defaultMessage: 'Eigener Datenschutzrichtlinien-Link (optional)' },
  DEFAULT_SMS_TEMPLATE_DE: {
    id: 'CompanyUpdateComponent.DEFAULT_SMS_TEMPLATE_DE',
    defaultMessage: 'Bitte klicken Sie auf den Link um eine tele-LOOK Verbindung aufzubauen:',
  },
  DEFAULT_SMS_TEMPLATE_EN: {
    id: 'CompanyUpdateComponent.DEFAULT_SMS_TEMPLATE_EN',
    defaultMessage: 'Please click the link to set up a tele-LOOK connection:',
  },
  DEFAULT_EMAIL_TEMPLATE_DE: {
    id: 'CompanyUpdateComponent.DEFAULT_EMAIL_TEMPLATE_DE',
    defaultMessage: 'Sehr geehrte Damen und Herren,\n\nhiermit lade ich Sie, wie besprochen, zum Video Call ein.\n\nBitte klicken Sie auf den nachfolgenden Link, um dem virtuellen Raum beizutreten:\n[LINK]\n\nMit freundlichen Grüßen',
  },
  DEFAULT_EMAIL_TEMPLATE_EN: {
    id: 'CompanyUpdateComponent.DEFAULT_EMAIL_TEMPLATE_EN',
    defaultMessage: 'Dear Sir or Madam,\n\nI hereby invite you to the video call as discussed.\n\nPlease click on the following link to join the virtual room:\n[LINK]\n\nBest regards',
  },
  LABEL_EMAIL_LANGUAGE: {
    id: 'CompanyUpdateComponent.LABEL_EMAIL_LANGUAGE',
    defaultMessage: 'Sprache der E-Mail',
  },
  LABEL_SMS_LANGUAGE: {
    id: 'CompanyUpdateComponent.LABEL_SMS_LANGUAGE',
    defaultMessage: 'Sprache der SMS',
  },
  LABEL_FROM: {
    id: 'CompanyUpdateComponent.LABEL_FROM',
    defaultMessage: 'Absender (From)',
  },
  EMAIL_SENDER_INFO: {
    id: 'CompanyUpdateComponent.EMAIL_SENDER_INFO',
    defaultMessage: 'Die E-Mail wird über Ihr verknüpftes E-Mail-Programm (z.B. Outlook) versendet. Der Absender ist daher immer Ihre eigene E-Mail-Adresse.',
  },
  SMS_FROM_PLACEHOLDER: {
    id: 'CompanyUpdateComponent.SMS_FROM_PLACEHOLDER',
    defaultMessage: 'Absender, max. 11 Zeichen',
  },
  PLACEHOLDER_LANGUAGE: {
    id: 'CompanyUpdateComponent.PLACEHOLDER_LANGUAGE',
    defaultMessage: 'Sprache',
  },
  PLACEHOLDER_EMAIL_TEMPLATE: {
    id: 'CompanyUpdateComponent.PLACEHOLDER_EMAIL_TEMPLATE',
    defaultMessage: 'Sehr geehrte Damen und Herren,\n\nhiermit lade ich Sie, wie besprochen, zum Video Call ein.\n\nBitte klicken Sie auf den nachfolgenden Link, um dem virtuellen Raum beizutreten:\n[LINK]\n\nMit freundlichen Grüßen',
  },
  PLACEHOLDER_SMS_TEMPLATE: {
    id: 'CompanyUpdateComponent.PLACEHOLDER_SMS_TEMPLATE',
    defaultMessage: 'Hallo, hier ist der Link zum Video-Call: [LINK]',
  },
  RESET_DEFAULT: {
    id: 'CompanyUpdateComponent.RESET_DEFAULT',
    defaultMessage: 'Auf Standard zurücksetzen',
  },
});

const defaultSmsTexts = {
  de: 'Bitte klicken Sie auf den Link um eine tele-LOOK Verbindung aufzubauen: [LINK]',
  en: 'Please click the link to set up a tele-LOOK connection: [LINK]',
  fr: 'Veuillez cliquer sur le lien pour établir une connexion tele-LOOK : [LINK]',
  es: 'Por favor, haga clic en el enlace para establecer una conexión tele-LOOK: [LINK]'
};

const defaultEmailTexts = {
  de: 'Sehr geehrte Damen und Herren,\n\nhiermit lade ich Sie, wie besprochen, zum Video Call ein.\n\nBitte klicken Sie auf den nachfolgenden Link, um dem virtuellen Raum beizutreten:\n[LINK]\n\nMit freundlichen Grüßen',
  en: 'Dear Sir or Madam,\n\nI hereby invite you to the video call as discussed.\n\nPlease click on the following link to join the virtual room:\n[LINK]\n\nBest regards',
  fr: 'Madame, Monsieur,\n\nComme convenu, je vous invite à participer à l\'appel vidéo.\n\nVeuillez cliquer sur le lien ci-dessous pour rejoindre la salle virtuelle :\n[LINK]\n\nCordialement',
  es: 'Estimado cliente,\n\nPor la presente le invito a la videollamada según lo acordado.\n\nPor favor, haga clic en el siguiente enlace para unirse a la sala virtual:\n[LINK]\n\nSaludos cordiales'
};

function CompanyUpdateComponent(props) {
  const { formatMessage } = useIntl();
  const { initialValues, onSubmit, isAdmin, mode = 'general' } = props;

  const [activeMailLang, setActiveMailLang] = React.useState((initialValues && initialValues.locale) || 'de');
  const [activeSmsLang, setActiveSmsLang] = React.useState((initialValues && initialValues.locale) || 'de');

  const locales = availableLocales.map(locale => ({
    value: locale,
    label: formatMessage(messages[`LOCALE_${locale.toUpperCase()}`]),
  }));

  const retentionOptions = [
    { value: 1, label: formatMessage(messages.RETENTION_1_DAY) },
    { value: 30, label: formatMessage(messages.RETENTION_1_MONTH) },
    { value: 90, label: formatMessage(messages.RETENTION_3_MONTHS) },
    { value: 180, label: formatMessage(messages.RETENTION_6_MONTHS) },
    { value: 365, label: formatMessage(messages.RETENTION_1_YEAR) },
    { value: 730, label: formatMessage(messages.RETENTION_2_YEARS) },
  ];

  const onSubmitLocal = (data) => {
    const dataSubmit = {};
    dataSubmit.name = data.name !== undefined ? data.name : initialValues.name;
    if (data.locale !== undefined) {
      dataSubmit.locale = data.locale && data.locale.value ? data.locale.value : data.locale;
    }
    if (data.templates !== undefined) {
      dataSubmit.templates = data.templates;
    }
    
    if (isAdmin) {
      if (data.trial_days !== undefined) {
        dataSubmit.trial_days = parseInt(data.trial_days, 10);
      }
      if (data.max_users !== undefined && data.max_users !== '') {
        dataSubmit.max_users = parseInt(data.max_users, 10);
      }
      if (data.allowed_languages !== undefined) {
        dataSubmit.allowed_languages = data.allowed_languages;
      }
      if (data.retention_days !== undefined && data.retention_days.value) {
        dataSubmit.retention_days = data.retention_days.value;
      }
      if (data.privacy_url !== undefined) {
        dataSubmit.privacy_url = data.privacy_url;
      }
    }
    return props.onSubmit(dataSubmit);
  }

  const formInitialValues = React.useMemo(() => {
    if (!initialValues) return {};
    
    const templates = {
      sms: {},
      email: {}
    };

    availableLocales.forEach(loc => {
      const smsData = (initialValues.templates && initialValues.templates.sms && initialValues.templates.sms[loc]) || {};
      templates.sms[loc] = {
        from: smsData.from || 'tele-LOOK',
        text: smsData.text || defaultSmsTexts[loc] || ''
      };

      const emailData = (initialValues.templates && initialValues.templates.email && initialValues.templates.email[loc]) || {};
      templates.email[loc] = {
        text: emailData.text || defaultEmailTexts[loc] || ''
      };
    });

    return {
      ...initialValues,
      locale: _.find(locales, locale => locale.value === initialValues.locale),
      templates,
      retention_days: _.find(retentionOptions, option => option.value === (initialValues.retention_days || 365)) || retentionOptions[4],
      privacy_url: initialValues.privacy_url || ''
    };
  }, [initialValues]);

  return (
    <Form
      onSubmit={onSubmitLocal}
      keepDirtyOnReinitialize
      initialValuesEqual={(a, b) => _.isEqual(a, b)}
      validate={values => {
        const errors = {};
        if (mode === 'general' && !values.name) {
          errors.name = formatMessage(messages.VALIDATION_FIELD_REQUIRED);
        }
        return errors;
      }}
      initialValues={formInitialValues}
    >
      {({ handleSubmit, submitError, submitting, pristine, form }) => {
        const smsLocales = locales.filter(l => l.value === 'de' || l.value === 'en');
        return (
        <form onSubmit={handleSubmit}>
          {mode === 'general' && (
            <>
          <Field
            name="name"
          >
            { ({ input, meta }) => {
              return (
                <FormGroup2 input={input} meta={meta}>
                  <TextField
                    {...input}
                    autoFocus
                    autoComplete="off"
                    placeholder={formatMessage(messages.PLACEHOLDER_COMPANY_NAME)}
                    style={{ color: 'var(--input-text)', backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', fontWeight: '500' }}
                  />
                </FormGroup2>
              );
            }}
          </Field>

          <div>
            <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_LANGUAGE)}</span>
            <Field
              name="locale"
            >
            { ({ input, meta }) => {
              return (
                <FormGroup2 input={input} meta={meta}>
                  <Select
                    {...input}
                    name="form-field-locale"
                    value={input.value}
                    options={smsLocales}
                    searchable={false}
                    clearable={false}
                      placeholder={formatMessage(messages.PLACEHOLDER_LANGUAGE)}
                  />
                </FormGroup2>
              );
            }}
            </Field>
          </div>

          {/* Moved templates below isAdmin block */}
          {isAdmin && (
            <div>
              <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_TRIAL_DAYS)}</span>
              <Field name="trial_days">
                { ({ input, meta }) => {
                  return (
                    <FormGroup2 input={input} meta={meta}>
                      <TextField
                        {...input}
                        type="number"
                        min="1"
                        autoComplete="off"
                        placeholder="14"
                        style={{ color: 'var(--input-text)', backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', fontWeight: '500' }}
                      />
                    </FormGroup2>
                  );
                }}
              </Field>



              <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_ALLOWED_LANGUAGES)}</span>
              <Field name="allowed_languages">
                { ({ input, meta }) => {
                  return (
                    <FormGroup2 input={input} meta={meta}>
                      <TextField
                        {...input}
                        type="text"
                        autoComplete="off"
                        placeholder="de,en,es"
                        style={{ color: 'var(--input-text)', backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', fontWeight: '500' }}
                      />
                    </FormGroup2>
                  );
                }}
              </Field>

              <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_RETENTION_DAYS)}</span>
              <Field name="retention_days">
                { ({ input, meta }) => {
                  return (
                    <FormGroup2 input={input} meta={meta}>
                      <Select
                        {...input}
                        name="form-field-retention-days"
                        value={input.value}
                        options={retentionOptions}
                        searchable={false}
                        clearable={false}
                      />
                    </FormGroup2>
                  );
                }}
              </Field>

              <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_PRIVACY_URL)}</span>
              <Field name="privacy_url">
                { ({ input, meta }) => {
                  return (
                    <FormGroup2 input={input} meta={meta}>
                      <TextField
                        {...input}
                        type="url"
                        autoComplete="off"
                        placeholder="https://..."
                        style={{ color: 'var(--input-text)', backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', fontWeight: '500' }}
                      />
                    </FormGroup2>
                  );
                }}
              </Field>
            </div>
          )}
          </>
          )}

          {mode === 'mail' && (
            <>
              <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_EMAIL_LANGUAGE)}</span>
              <div style={{ marginBottom: '16px' }}>
                <Select
                  name="form-field-email-language"
                  value={locales.find(l => l.value === activeMailLang)}
                  options={locales}
                  searchable={false}
                  clearable={false}
                  placeholder={formatMessage(messages.PLACEHOLDER_LANGUAGE)}
                  onChange={(selected) => {
                    setActiveMailLang(selected.value);
                  }}
                />
              </div>

              <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_FROM)}</span>
              <div style={{ padding: '16px', backgroundColor: 'var(--hover-bg, rgba(0, 0, 0, 0.02))', borderLeft: '4px solid var(--primary-color, #3b82f6)', borderRadius: '8px', color: 'var(--text-color)', fontSize: '14px', lineHeight: '1.5', marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <i className="fa fa-info-circle" style={{ color: 'var(--primary-color, #3b82f6)', fontSize: '18px', marginTop: '2px', flexShrink: 0 }} />
                <div>{formatMessage(messages.EMAIL_SENDER_INFO)}</div>
              </div>

              <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_EMAIL_TEMPLATE)}</span>
              <Field name={`templates.email.${activeMailLang}.text`}>
                { ({ input, meta }) => {
                  return (
                    <FormGroup2 input={input} meta={meta}>
                      <textarea
                        {...input}
                        className="form-control"
                        rows="8"
                        placeholder={formatMessage(messages.PLACEHOLDER_EMAIL_TEMPLATE)}
                        style={{ color: 'var(--input-text)', backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', fontWeight: '500', marginBottom: '8px' }}
                      />
                    </FormGroup2>
                  );
                }}
              </Field>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-8px', marginBottom: '32px' }}>
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  style={{ padding: 0, fontSize: '13px', color: 'var(--primary-color, #3b82f6)', textDecoration: 'none' }}
                  onClick={() => {
                    form.change(`templates.email.${activeMailLang}.text`, defaultEmailTexts[activeMailLang] || '');
                  }}
                >
                  <i className="fa fa-undo" style={{ marginRight: '6px' }} />
                  {formatMessage(messages.RESET_DEFAULT)}
                </button>
              </div>
            </>
          )}

          {mode === 'sms' && (
            <>
              <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_SMS_LANGUAGE)}</span>
              <div style={{ marginBottom: '16px' }}>
                <Select
                  name="form-field-sms-language"
                  value={locales.find(l => l.value === activeSmsLang)}
                  options={locales}
                  searchable={false}
                  clearable={false}
                  placeholder={formatMessage(messages.PLACEHOLDER_LANGUAGE)}
                  onChange={(selected) => {
                    setActiveSmsLang(selected.value);
                  }}
                />
              </div>

              <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_FROM)}</span>
              <Field name={`templates.sms.${activeSmsLang}.from`}>
                { ({ input, meta }) => {
                  return (
                    <FormGroup2 input={input} meta={meta}>
                      <TextField
                        {...input}
                        type="text"
                        maxLength="11"
                        autoComplete="off"
                        placeholder={formatMessage(messages.SMS_FROM_PLACEHOLDER)}
                        style={{ color: 'var(--input-text)', backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', fontWeight: '500' }}
                      />
                    </FormGroup2>
                  );
                }}
              </Field>

              <span style={{ display: 'block', marginTop: '16px', fontWeight: 'bold', color: 'var(--text-color)' }}>{formatMessage(messages.LABEL_SMS_TEMPLATE)}</span>
              <Field name={`templates.sms.${activeSmsLang}.text`}>
                { ({ input, meta }) => {
                  return (
                    <FormGroup2 input={input} meta={meta}>
                      <textarea
                        {...input}
                        className="form-control"
                        rows="4"
                        maxLength="123"
                        placeholder={formatMessage(messages.PLACEHOLDER_SMS_TEMPLATE)}
                        style={{ color: 'var(--input-text)', backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', fontWeight: '500', marginBottom: '8px' }}
                      />
                    </FormGroup2>
                  );
                }}
              </Field>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-8px', marginBottom: '32px' }}>
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  style={{ padding: 0, fontSize: '13px', color: 'var(--primary-color, #3b82f6)', textDecoration: 'none' }}
                  onClick={() => {
                    form.change(`templates.sms.${activeSmsLang}.text`, defaultSmsTexts[activeSmsLang] || '');
                    form.change(`templates.sms.${activeSmsLang}.from`, 'tele-LOOK');
                  }}
                >
                  <i className="fa fa-undo" style={{ marginRight: '6px' }} />
                  {formatMessage(messages.RESET_DEFAULT)}
                </button>
              </div>
            </>
          )}

          <FormError error={submitError} />
          <FormSubmit
            btnClass="lh-btn lh-btn-primary"
            submitting={submitting}
            label={formatMessage(messages.LABEL_SAVE_CHANGES)}
          />
        </form>
      );
    }}
    </Form>
  );
}

/*
const CompanyUpdateComponentReduxForm = reduxForm({
  form: 'companyUpdate',
  fields: ['name', 'locale'],
  validate: (values, { intl: { formatMessage } }) => ({
    name: values.name ? null : formatMessage(messages.VALIDATION_FIELD_REQUIRED),
  }),
  touchOnBlur: false,
})(CompanyUpdateComponent);
*/
export default injectIntl(CompanyUpdateComponent);
